import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

type KonvaImagePromisesContextType = {
  getPromiseResolver: (path: string) => {
    resolve: () => void;
    reject: () => void;
    promise: Promise<void>;
  };
  removePromise: (promise: Promise<void>) => void;
};

type KonvaImagePromisesProviderProps = PropsWithChildren<{
  onDownloadableChange?: (downloadable: boolean) => void;
}>;

const KonvaImagePromisesContext = createContext<
  KonvaImagePromisesContextType | undefined
>(undefined);

export const KonvaImagePromisesProvider: FC<
  KonvaImagePromisesProviderProps
> = ({ children, onDownloadableChange }) => {
  const promisesRef = useRef<
    Map<
      Promise<void>,
      {
        active: boolean;
        status: 'pending' | 'resolved' | 'rejected';
        path: string; // for debugging
      }
    >
  >(new Map());
  const [isProcessing, setIsProcessing] = useState(false);

  const triggerUpdate = useCallback(() => {
    setIsProcessing(prev => !prev);
  }, []);

  const getPromiseResolver = useCallback(
    (path: string) => {
      const { promise, resolve, reject } = Promise.withResolvers<void>();

      promisesRef.current.set(promise, {
        active: true,
        status: 'pending',
        path
      });

      return {
        promise,
        resolve: () => {
          const data = promisesRef.current.get(promise);
          if (data) {
            data.status = 'resolved';
            triggerUpdate();
          }
          resolve();
        },
        reject: () => {
          const data = promisesRef.current.get(promise);
          if (data) {
            data.status = 'rejected';
            triggerUpdate();
          }
          reject();
        }
      };
    },
    [triggerUpdate]
  );

  const removePromise = useCallback(
    (promise: Promise<void>) => {
      promisesRef.current.delete(promise);
      triggerUpdate();
    },
    [triggerUpdate]
  );

  useEffect(() => {
    const allPromises = Array.from(promisesRef.current.entries()).filter(
      ([, { active }]) => active
    );

    Promise.allSettled(allPromises.map(([promise]) => promise)).then(() => {
      const allSettled = allPromises.every(
        ([, { status }]) => status !== 'pending'
      );

      onDownloadableChange?.(allSettled);
    });
  }, [isProcessing, onDownloadableChange]);

  const contextValue = useMemo(
    () => ({ getPromiseResolver, removePromise }),
    [getPromiseResolver, removePromise]
  );

  return (
    <KonvaImagePromisesContext.Provider value={contextValue}>
      {children}
    </KonvaImagePromisesContext.Provider>
  );
};

export const useKonvaImagePromisesContext = () => {
  const context = useContext(KonvaImagePromisesContext);
  if (!context) {
    throw new Error(
      'useKonvaImagePromisesContext must be used within a KonvaImagePromisesProvider'
    );
  }
  return context;
};
